import { RouteLocationNormalized } from "vue-router";
import { useUser } from "@/store/pinia/userStore";
import { EntityStatus } from "@/models/enums/EntityStatus";
import { findAvailableBackstageRetrial } from "@/models/subscriptions/SubscriptionOfferDto";
import { Production } from "@/models/productions/Production";
import { Organization } from "@/models/organizations/Organization";
import { BackstageSubscriptionDto } from "@/models/subscriptions/BackstageSubscriptionDto";

function getSubscriptionRedirect(subStatus: BackstageSubscriptionDto["status"] | undefined, production: Production, organizations: Organization[]) {
	if (subStatus === "trial_ended") {
		const organization = organizations.find((org) => org.organizationId === production.organizationId);
		return findAvailableBackstageRetrial(organization?.subscriptionOffers)
			? {
					name: "organization-settings-subscription",
					params: { organizationId: production.organizationId },
				}
			: {
					name: "production-subscription-notice",
					params: { productionId: production.productionId },
				};
	}

	if (subStatus === "past_due" || subStatus === "unpaid") {
		return {
			name: "organization-settings-subscription",
			params: { organizationId: production.organizationId },
		};
	}

	return {
		name: "production-activation-start",
		params: { productionId: production.productionId },
	};
}

export async function checkProductionSubscription(to: RouteLocationNormalized) {
	// Skip check for activation-related routes
	if (to.name?.toString().includes("activation")) {
		return true;
	}

	const productionId = to.params.productionId as string;
	if (!productionId) {
		return true;
	}

	try {
		const userStore = useUser();

		// 🔑 Ensure the store is hydrated exactly once (no re-fetch on later guards)
		await userStore.ensureHydrated();

		const production = userStore.productionData.find((p) => p.id === productionId);
		if (!production) {
			// If it still isn't found, don't block navigation-fail open
			return true;
		}

		if (production.status === EntityStatus.Archived) {
			return true;
		}

		const subStatus = production.subscription?.status;

		if (subStatus !== "active" && subStatus !== "trialing" && userStore.canManage(userStore.currentEntity)) {
			return getSubscriptionRedirect(subStatus, production, userStore.organizationData);
		}

		return true;
	} catch (error) {
		console.error("Error checking production subscription:", error);
		return true; // fail open on errors
	}
}
